import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';

import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';

export type EligibleUser = {
  email: string;
  name: string;
};

export type ActionBlockedDialogProps = {
  open: boolean;
  onClose: () => void;
  eligibleUsers: EligibleUser[];
  action: 'merge' | 'discard';
};

export const ActionBlockedDialog: React.FC<ActionBlockedDialogProps> = ({ open, onClose, eligibleUsers, action }) => {
  const handleCopyEmail = ({ email }: EligibleUser) => {
    navigator.clipboard.writeText(email);
    SNACKBAR_STORE.getState().showSnackbar('Email copied to clipboard', 'success', true);
  };

  return (
    <Dialog
      id='action-blocked-dialog'
      data-testid='action-blocked-dialog'
      open={open}
      onClose={onClose}
      scroll='paper'
      fullWidth
    >
      <DialogTitle variant='h6'>{action === 'merge' ? 'Merge' : 'Discard'} blocked</DialogTitle>
      <DialogContent>
        {eligibleUsers.length > 0 ? (
          <>
            <Typography>
              Some changes are outside your edit rights. The following people have the rights to {action} this proposal
              on your behalf:
            </Typography>
            <List data-testid='eligible-users-list'>
              {eligibleUsers.map((user) => (
                <ListItem
                  key={user.email}
                  data-testid={`eligible-user-item-${user.email}`}
                  secondaryAction={
                    <Tooltip title='Copy email'>
                      <IconButton
                        edge='end'
                        aria-label='copy email'
                        data-testid={`copy-email-button-${user.email}`}
                        onClick={() => handleCopyEmail(user)}
                      >
                        <ContentCopyOutlined />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText primary={user.name} secondary={user.email} />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <Typography>
            Some changes are outside your edit rights. No users are currently available to {action} this proposal.
            Contact your project administrator.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' data-testid='action-blocked-cancel-button' onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
